import React, { useEffect, useState } from "react";
import { X } from "react-feather";
import Icon from "@mdi/react";
import {
  mdiAlert,
  mdiAlertOutline,
  mdiCalendarBlankOutline,
  mdiHistory,
  mdiPackageVariantClosed,
  mdiWarehouse,
} from "@mdi/js";
import {
  formatAmountWithCommas,
  formatDateYYYYMMDD,
} from "../../content_panel/utils/helpers";
import LinkedInwardsTable from "./LinkedInwards";
import CurrencyBadge from "./CurrencyBadge";
import LoadingWidget from "../../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import DeviceIdsViewer from "../common-components/DeviceIdsViewer";
import {
  ItemWarehouseModel,
  WarehouseInwardModel,
  LinkedInwardModel,
} from "../model/warehouse_inward_models";
import PastDisputes from "./PastDisputes";
import {
  FetchPOInwardOrderDetailsByIdRepo,
  FetchSTInwardOrderDetailsByIdRepo,
  GetLinkedInwardForPOAPI,
} from "../warehouse_module_repo";
import "../inward-styles.css";
import {
  getInwardStatusClass,
  computeInwardTaxTotals,
} from "../warehouse_helper";

interface InwardViewModalProps {
  inward_order: WarehouseInwardModel;
  isOpen: boolean;
  onClose: () => void;
}

type MainTabType = "items" | "grn-history";

const InwardViewModal: React.FC<InwardViewModalProps> = ({
  inward_order,
  isOpen,
  onClose,
}) => {
  const [inwardList, setInwardList] = useState<LinkedInwardModel[]>([]);
  const [detailedOrder, setDetailedOrder] =
    useState<WarehouseInwardModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<MainTabType>("items");

  const { showToast } = useToast();

  const order = detailedOrder ?? inward_order;

  const isRevised =
    inward_order.inward_order_id?.toLowerCase()?.startsWith("po-") &&
    inward_order.inward_order_id?.split("-")?.length > 2;

  const fetchLinkedInwardData = async () => {
    try {
      setLoading(true);

      const ref_type = inward_order.type === "PO" ? "GRN" : "Stock transfer";

      const res = await GetLinkedInwardForPOAPI(
        inward_order.inward_order_id,
        ref_type,
      );

      if (res.success) {
        setInwardList(res.data);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailedOrder = async () => {
    try {
      setLoading(true);
      const fetcher =
        inward_order.type === "PO"
          ? FetchPOInwardOrderDetailsByIdRepo
          : FetchSTInwardOrderDetailsByIdRepo;
      const res = await fetcher(inward_order.inward_order_id);
      if (res?.success && res.data) {
        setDetailedOrder(res.data as WarehouseInwardModel);
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!inward_order?.inward_order_id) return;
    setDetailedOrder(null);
    fetchDetailedOrder();
    fetchLinkedInwardData();
  }, [inward_order?.inward_order_id]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen || !inward_order) return null;

  return (
    <div
      id="warehouse-inward-view-modal"
      className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto flex items-start justify-center p-4 py-10"
    >
      <div className="relative bg-white rounded-lg shadow-lg w-full max-w-4xl wh-modal-xs">
        <button
          id="warehouse-inward-view-modal-close"
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-10"
        >
          <X size={20} />
        </button>

        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-3">
            <span
              className={isRevised ? "border-l-4 border-l-primary pl-2" : ""}
            >
              {order?.inward_order_id}
            </span>

            <span className="px-2.5 py-0.5 rounded-full border text-xs font-semibold">
              {order.type}
            </span>

            <span
              className={`px-2.5 py-0.5 rounded-full border text-xs font-semibold capitalize ${getInwardStatusClass(
                order?.status,
              )}`}
            >
              {order?.status}
            </span>

            {isRevised && (
              <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors status-revised">
                <Icon path={mdiHistory} size={0.6} className="mr-1" />
                Revised
              </span>
            )}

            {order.past_disputes &&
              order.past_disputes.some((d) => d.status === "open") && (
                <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors status-disputed">
                  <Icon path={mdiAlertOutline} size={0.6} className="mr-1" />
                  Disputed
                </span>
              )}
          </h2>

          {order.past_disputes && order.past_disputes.length > 0 && (
            <div className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-100 p-4 mt-4">
              <Icon
                path={mdiAlert}
                size={1}
                className="text-color-destructive shrink-0 mt-0.5"
              />

              <div className="text-sm flex-1">
                <p className="font-medium text-color-destructive">
                  GRN Creation Blocked
                </p>
                <p className="text-muted-foreground mt-1">
                  This PO has unresolved disputes. No further GRNs can be
                  created until a Revised PO is issued.
                </p>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="grid grid-cols-4 gap-4 text-sm mt-6">
            <div>
              <p className="text-gray-500 flex gap-1">
                <Icon path={mdiWarehouse} className="h-4 w-4 text-blue-600" />
                From
              </p>
              <p className="font-semibold">{order?.from?.name}</p>
            </div>

            <div>
              <p className="text-gray-500 flex gap-1">
                <Icon path={mdiWarehouse} className="h-4 w-4 text-green-600" />
                To Warehouse
              </p>
              <p className="font-semibold">{order?.toWarehouse?.name}</p>
            </div>

            <div>
              <p className="text-gray-500 flex gap-1">
                <Icon
                  path={mdiCalendarBlankOutline}
                  className="h-4 w-4 text-purple-600"
                />
                Expected Date
              </p>
              <p className="font-semibold">
                {formatDateYYYYMMDD(order?.expectedDate)}
              </p>
            </div>

            <div>
              <p className="text-gray-500 flex gap-1">
                <Icon
                  path={mdiPackageVariantClosed}
                  className="h-4 w-4 text-orange-600"
                />
                Total Value
              </p>
              <p className="font-semibold">
                ₹ {formatAmountWithCommas(order?.totalPrice)}
                <CurrencyBadge
                  currency={order?.currency}
                  rate={order?.vendor_currency_to_inr}
                  inrAmount={order?.totalPrice}
                />
              </p>
              {computeInwardTaxTotals(order?.items).gst > 0 && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Incl. GST: ₹{" "}
                  {formatAmountWithCommas(
                    computeInwardTaxTotals(order?.items).total,
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 mt-4">
          <div className="flex w-full gap-2 bg-blue-100 p-1.5 rounded-md">
            {[
              {
                key: "items",
                label: `Items (${order.items?.length || 0})`,
              },
              {
                key: "grn-history",
                label: `GRN History (${inwardList ? inwardList.length : 0})`,
              },
            ].map((t) => (
              <button
                key={t.key}
                id={`warehouse-inward-view-modal-tab-${t.key}`}
                onClick={() => setActiveTab(t.key as MainTabType)}
                className={`px-4 w-1/2 py-1.5 rounded-md text-sm font-bold transition-all ${
                  activeTab === t.key
                    ? "bg-white shadow-sm text-blue-600"
                    : "text-gray-600 hover:bg-white/50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === "items" && (
            <div className="space-y-6">
              <PastDisputes disputes={order.past_disputes || []} />

              <div className="space-y-4">
                <h3 className="font-bold flex gap-1 items-center">
                  <Icon
                    path={mdiPackageVariantClosed}
                    className="h-5 w-5 text-orange-600"
                  />
                  Items
                </h3>

                {(order.items ?? []).map(
                  (item: ItemWarehouseModel, idx: number) => (
                    <div
                      key={idx}
                      className="border bg-gray-50 rounded-lg p-4 space-y-3"
                    >
                      <div className="flex justify-between">
                        <div className="min-w-0 pr-4">
                          <p className="font-semibold">{item.itemName}</p>
                          <p className="text-sm text-gray-500">
                            {item.inventoryItemCode}
                          </p>
                          {item.itemDescription && (
                            <p
                              className="text-xs text-gray-500 italic mt-1 line-clamp-2"
                              title={item.itemDescription}
                            >
                              {item.itemDescription}
                            </p>
                          )}
                        </div>
                        <div>
                          <div className="font-semibold">
                            ₹{" "}
                            {formatAmountWithCommas(
                              (item?.itemPrice ?? 0) * item.requestedQty,
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            @ ₹ {formatAmountWithCommas(item?.itemPrice ?? 0)}
                            /unit
                          </div>
                          <CurrencyBadge
                            currency={order?.currency}
                            rate={order?.vendor_currency_to_inr}
                            inrAmount={item?.itemPrice ?? 0}
                            perUnit
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Ordered</p>
                          <p className="font-semibold">{item.requestedQty}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Received</p>
                          <p className="font-semibold text-success">
                            {item.receivedQty}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Pending</p>
                          <p className="font-semibold text-warning">
                            {(item.requestedQty ?? 0) - (item.receivedQty ?? 0)}
                          </p>
                        </div>
                      </div>
                      {item.deviceIds && item.deviceIds.length > 0 && (
                        <DeviceIdsViewer
                          deviceIds={item.deviceIds}
                          title="Allocated Devices"
                        />
                      )}
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {activeTab === "grn-history" && (
            <div className="space-y-4">
              <LinkedInwardsTable data={inwardList} />
              {inwardList.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                  <Icon
                    path={mdiPackageVariantClosed}
                    size={2}
                    className="opacity-20 mb-2"
                  />
                  <p>No GRNs recorded yet</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-end">
          <button
            id="warehouse-inward-view-modal-close-btn"
            onClick={onClose}
            className="border px-4 py-2 rounded hover:bg-gray-50 font-semibold"
          >
            Close
          </button>
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
};

export default InwardViewModal;
